import { useEffect, useRef, useState } from "react";
import { Formik, FormikProps } from "formik";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import * as Yup from "yup";
import { useQuery } from "@tanstack/react-query";
import useCustomers from "../../../hooks/CustomersHook";
import { Customer } from "../../../components/models/customer/Customer";
import { PASSBOOK_SALE_REASON_ENUM, PAYMENT_MODES } from "../../../resources/enums";
import { PassbookSale } from "../../../resources/types";
import { ICreatePassbookSaleDTO } from "../../../resources/interfaces";
import { SERVER_DATE_FORMAT } from "../../../resources/constants";
import moment from "moment";
import useSearchQuery from "../../../hooks/SearchQueryHook";
import Button from "../../../components/form-control/Button";
import TextArea from "../../../components/form-control/TextArea";
import SelectDropdown from "../../../components/form-control/SelectDropdown";
import { getPhoneNumberFromShortCodeFormat, getSelectItemsFromEnum } from "../../../utilities/miscFunctions";
import Input from "../../../components/form-control/Input";
import CustomerComponent from "../CustomerComponent";
import InputSelectDropdown from "../../../components/form-control/InputSelectDropdown";
import Avatar from "../../../components/elements/Avatar";
import { createPassbookSale } from "../../../api/requests";
import DatePickerInput from "../../../components/form-control/DatePicker/DatePickerInput";

interface IProps {
    customer?: Customer
    onComplete: (passbookSale: PassbookSale) => void;
    shouldReset?: boolean;
}

const SellPassbook = (props: IProps): JSX.Element => {
    const { customers, fetchCustomers, customer, fetchCustomerById } = useCustomers();
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(props.customer ?? null);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const { debouncedQuery, setQuery } = useSearchQuery();

    const formikRef = useRef<FormikProps<any>>(null);

    const { data, isLoading, isStale, isFetching } = useQuery<any, Error>({
        queryKey: [
            'customers',
            debouncedQuery,
        ],
        queryFn: () => fetchCustomers({ query: debouncedQuery })
    });

    useEffect(() => {
        if (customer) {
            setSelectedCustomer(customer);
        }
    }, [customer])

    const initialValues = {
        customer_id: props.customer?.id ?? "",
        amount: 0,
        sale_reason: PASSBOOK_SALE_REASON_ENUM.NEW,
        sale_date: new Date(),
        payment_method: PAYMENT_MODES.CASH,
        notes: "",
    }

    const submitHandler = (values: ICreatePassbookSaleFormData) => {
        const payload: ICreatePassbookSaleDTO = {
            ...values,
            sale_date: moment(values.sale_date).format(SERVER_DATE_FORMAT)
        }

        setSubmitting(true);
        createPassbookSale(payload)
            .then((response: PassbookSale) => {

                if (response) {
                    props.onComplete(response);

                    if (props.shouldReset) {
                        formikRef.current?.resetForm();
                    }
                }
                toast.success(`${"Passbook sale recorded successfully"}`, { autoClose: 4000 });
            }).catch((error: any | AxiosError | Error) => {
                toast.error(error?.detail ?? error?.description, { autoClose: 5000 });
            }).finally(() => setSubmitting(false))
    }

    return (
        <div>
            {selectedCustomer && (
                <div className="flex flex-col justify-center items-center text-xs">
                    <Avatar
                        src={selectedCustomer?.picture_url}
                    />
                    <span className="font-bold">{selectedCustomer.name} ({selectedCustomer.type})</span>
                    <span><b>Phone:</b> {getPhoneNumberFromShortCodeFormat(selectedCustomer.phone)}</span>
                    <span><b>Location:</b> {selectedCustomer.address?.street_address}, {selectedCustomer?.address?.city}</span>

                </div>
            )}
            <Formik
                initialValues={initialValues}
                innerRef={formikRef}
                validationSchema={Yup.object({
                    amount: Yup.number()
                        .required('Amount is required'),
                    customer_id: Yup.string()
                        .trim()
                        .required('Customer is required'),
                    payment_method: Yup.string()
                        .trim()
                        .required('Payment method is required'),
                    sale_reason: Yup.string()
                        .trim()
                        .required('Sale reason is required is required'),
                    sale_date: Yup.date()
                        .required("Passbook sale date is required")
                })}
                onSubmit={(values: ICreatePassbookSaleFormData) => {
                    submitHandler(values);
                }}>
                {({ isSubmitting, values, handleBlur, handleChange, handleSubmit, setFieldValue, touched, errors }) => (
                    <form onSubmit={handleSubmit} className="mt-4">
                        <div className="flex flex-col gap-4">
                            <InputSelectDropdown
                                list={customers?.map((customer: Customer) => (
                                    {
                                        name: <CustomerComponent
                                            customer={customer}
                                            avatarSize="xs"
                                            metaData={<div className="text-xs">
                                                {customer.type} | {getPhoneNumberFromShortCodeFormat(customer.phone)} | Accounts: {customer.total_accounts}
                                            </div>}
                                        />,
                                        value: customer.id,
                                        id: customer.id,
                                        label: customer.name
                                    }
                                ))}
                                name="customer_id"
                                listSelectedValue={props.customer && props.customer.id ? props.customer.id : values.customer_id}
                                onChange={(value: string) => {
                                    if (value) {
                                        const _customer = customers.find((customer: Customer) => customer.id === value);
                                        setSelectedCustomer(_customer ?? null);

                                        if (!_customer) {
                                            fetchCustomerById(value);
                                        }

                                    } else {
                                        setSelectedCustomer(null);
                                    }
                                    setFieldValue("customer_id", value);
                                }}
                                label="Customer"
                                // wrapperClasses="w-full rounded"
                                placeholder="Search by name or phone number"
                                disabled={!!props.customer}
                                onSearchChange={setQuery}
                                error={touched.customer_id ? errors.customer_id : undefined
                                }
                                helperText={touched?.customer_id ? errors.customer_id : undefined
                                }
                            />

                            <Input
                                type="number"
                                value={values.amount}
                                name="amount"
                                label="Amount"
                                placeholder="Enter amount"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                required
                                error={touched.amount ? errors.amount : undefined
                                }
                                helperText={touched?.amount ? errors.amount : undefined
                                }
                                pattern="\d*"
                                min={1}
                            />

                            <SelectDropdown
                                list={getSelectItemsFromEnum(PASSBOOK_SALE_REASON_ENUM)}
                                listSelectedValue={values.sale_reason}
                                onChange={(value: string) => {
                                    setFieldValue("sale_reason", value);
                                }}
                                label="Sale Reason"
                                wrapperClasses="w-full rounded"
                                placeholder="Select reason"
                                error={touched.sale_reason ? errors.sale_reason : undefined
                                }
                                helperText={touched?.sale_reason ? errors.sale_reason : undefined
                                }
                            />

                            <SelectDropdown
                                list={getSelectItemsFromEnum(PAYMENT_MODES)}
                                listSelectedValue={values.payment_method}
                                onChange={(value: string) => {
                                    setFieldValue("payment_method", value);
                                }}
                                label="Payment Method"
                                wrapperClasses="w-full rounded"
                                placeholder="Select payment method"
                                required
                                error={touched.payment_method ? errors.payment_method : undefined
                                }
                                helperText={touched?.payment_method ? errors.payment_method : undefined
                                }
                            />

                            <DatePickerInput
                                id="dateOfBirth"
                                selected={values.sale_date}
                                onChange={(date: Date | null) => {
                                    setFieldValue("sale_date", date);
                                }}
                                dateFormat="dd-MM-yyyy"
                                label="Sale Date"
                                showYearDropdown
                                placeholder="Enter sale date"
                                allowFutureSelections={false}
                                error={touched.sale_date && errors.sale_date !== undefined ? errors.sale_date : undefined}
                                helperText={touched.sale_date ? errors.sale_date : undefined}
                            />

                            {values.sale_reason === PASSBOOK_SALE_REASON_ENUM.OTHER && (
                                <TextArea
                                    value={values.notes}
                                    name="notes"
                                    label="Notes"
                                    placeholder="Enter notes"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={touched.notes ? errors.notes : undefined
                                    }
                                    helperText={touched?.notes ? errors.notes : undefined
                                    }
                                    rows={2}
                                />
                            )}

                        </div>

                        <div className="flex justify-end mt-8">
                            <Button
                                label="Submit"
                                type="submit"
                                onClick={handleSubmit}
                                disabled={submitting}
                                loading={submitting}
                                className="px-12 bg-secondary-main rounded hover:bg-white hover:border-secondary-main"
                            />
                        </div>
                    </form>
                )}
            </Formik>
        </div>
    )
}

export default SellPassbook;

export interface ICreatePassbookSaleFormData {
    customer_id: string;
    amount: number;
    notes: string;
    payment_method: PAYMENT_MODES;
    sale_date: Date | null;
    sale_reason: PASSBOOK_SALE_REASON_ENUM
}